import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  AfterViewChecked,
  ChangeDetectorRef,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store';
import { Observable, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import * as CartSelectors from 'src/app/store/cart/cart.selectors';
import * as CartActions from 'src/app/store/cart/cart.actions';
import { StoreCart, CartItem } from 'src/app/store/cart/cart.models';
import { Router } from '@angular/router';
import { BusinessService } from 'src/app/services/business/business.service';
import { BusinessServiceService } from 'src/app/services/business-service/business-service.service';
import { StaffService } from 'src/app/services/staff/staff.service';
import { AppointmentService } from 'src/app/services/appointment/appointment.service';
import { BusinessInformationEntity } from 'src/app/models/business.model';
import { ServiceComboEntity } from 'src/app/models/service-combo.model';
import { StaffEntity } from 'src/app/models/staff.model';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';

declare var $: any;

interface SelectedService {
  serviceId: string;
  serviceName: string;
  staffId?: string;
  staffName?: string;
  price: number;
  duration: number;
}

@Component({
  selector: 'app-restaurantrightsidebar',
  templateUrl: './restaurantrightsidebar.component.html',
  styleUrls: ['./restaurantrightsidebar.component.css'],
})
export class RestaurantrightsidebarComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() storeId: string | null = null;

  storeCarts$: Observable<StoreCart[]>;
  cartItemCount$: Observable<number>;
  cartTotalPrice$: Observable<number>;
  isCartEmpty$: Observable<boolean>;

  // Booking state
  store: BusinessInformationEntity | null = null;
  services: ServiceComboEntity[] = [];
  staffs: StaffEntity[] = [];
  selectedServices: SelectedService[] = [];
  selectedDate: Date | null = null;
  selectedTime: string | null = null;
  availableTimes: string[] = [];
  dateInputValue: string = ''; // For ngModel two-way binding
  
  isLoadingStore = false;
  isLoadingServices = false;
  isLoadingStaff = false;
  staffAvailability: Map<string, boolean> = new Map(); // Map of staffId to availability status
  isLoadingAvailability = false;
  private subscriptions = new Subscription();

  constructor(
    private ngRxStore: Store<AppState>,
    private router: Router,
    private businessService: BusinessService,
    private businessServiceService: BusinessServiceService,
    private staffService: StaffService,
    private appointmentService: AppointmentService
  ) {
    this.storeCarts$ = this.ngRxStore.select(
      CartSelectors.selectStoreCartsArray
    );
    this.cartItemCount$ = this.ngRxStore.select(
      CartSelectors.selectCartItemCount
    );
    this.cartTotalPrice$ = this.ngRxStore.select(
      CartSelectors.selectCartTotalPrice
    );
    this.isCartEmpty$ = this.ngRxStore.select(CartSelectors.selectIsCartEmpty);
  }

  ngOnInit(): void {
    if (this.storeId) {
      this.loadStoreData();
    }
    // Load selected services from cart
    this.loadSelectedServicesFromCart();
    // Initialize dateInputValue if selectedDate exists
    if (this.selectedDate) {
      this.dateInputValue = this.formatDateForInput(this.selectedDate);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['storeId'] &&
      changes['storeId'].currentValue &&
      !changes['storeId'].firstChange
    ) {
      this.loadStoreData();
      this.loadSelectedServicesFromCart();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  loadStoreData(): void {
    if (!this.storeId) return;
    
    this.loadStore();
    this.loadServices();
    this.loadStaff();
  }

  loadStore(): void {
    if (!this.storeId) return;
    
    this.isLoadingStore = true;
    this.subscriptions.add(
      this.businessService
        .findById(this.storeId)
        .pipe(
          catchError((error) => {
            console.error('Error loading store:', error);
            this.isLoadingStore = false;
            return of(null);
          })
        )
        .subscribe((store) => {
          this.store = store;
          this.isLoadingStore = false;
          if (this.selectedDate) {
            this.generateAvailableTimes();
          }
        })
    );
  }

  loadServices(): void {
    if (!this.storeId) return;
    
    this.isLoadingServices = true;
    this.subscriptions.add(
      this.businessServiceService
        .getCombosForStore(this.storeId)
        .pipe(
          catchError((error) => {
            console.error('Error loading services:', error);
            this.isLoadingServices = false;
            return of([]);
          })
        )
        .subscribe((services) => {
          this.services = services.filter(
            (s) => s.status === 'active' && s.isFeature
          );
          this.isLoadingServices = false;
        })
    );
  }

  loadStaff(): void {
    if (!this.storeId) return;
    
    this.isLoadingStaff = true;
    this.subscriptions.add(
      this.staffService
        .getStaffByStoreId(this.storeId)
        .pipe(
          catchError((error) => {
            console.error('Error loading staff:', error);
            this.isLoadingStaff = false;
            return of([]);
          })
        )
        .subscribe((staffs) => {
          this.staffs = staffs.filter((s) => s.status === 'active');
          this.isLoadingStaff = false;
          
          // Check availability if date and time are already selected
          if (this.selectedDate && this.selectedTime) {
            this.checkStaffAvailability();
          }
        })
    );
  }

  loadSelectedServicesFromCart(): void {
    if (!this.storeId) return;

    // Subscribe to cart changes and load services for this store
    this.subscriptions.add(
      this.ngRxStore
        .select(CartSelectors.selectStoreCart(this.storeId))
        .subscribe((storeCart) => {
        if (storeCart && storeCart.items.length > 0) {
          // Convert cart items to selected services
          // Preserve existing staff selections if they match
          const newSelectedServices = storeCart.items.map((item) => {
              const service =
                item.combos && item.combos.length > 0 ? item.combos[0] : null;
            const serviceId = service?.id || item.id;
            
            // Try to preserve existing staff selection if service matches
              const existingService = this.selectedServices.find(
                (s) => s.serviceId === serviceId
              );
            
            const selectedService: SelectedService = {
              serviceId: serviceId,
              serviceName: service?.name || 'Service',
              // Prefer cart's staffId, but fall back to existing if cart doesn't have it yet
              staffId: item.staffId || existingService?.staffId,
              staffName: item.staffName || existingService?.staffName,
              price: item.totalPrice,
                duration: item.duration,
            };
            
            // If cart item has date/time, sync to selectedDate/selectedTime
            if (item.date && !this.selectedDate) {
                const itemDate =
                  typeof item.date === 'string'
                    ? new Date(item.date)
                    : item.date;
              if (!isNaN(itemDate.getTime())) {
                this.selectedDate = itemDate;
                  this.dateInputValue = this.formatDateForInput(itemDate);
                this.generateAvailableTimes();
              }
            }
            
            if (item.startTime && !this.selectedTime) {
                const itemTime =
                  typeof item.startTime === 'string'
                    ? new Date(item.startTime)
                    : item.startTime;
              if (!isNaN(itemTime.getTime())) {
                  this.selectedTime = `${String(itemTime.getHours()).padStart(
                    2,
                    '0'
                  )}:00`;
              }
            }
            
            return selectedService;
          });
          
          this.selectedServices = newSelectedServices;
          
          // Check staff availability if date and time are set and staff are loaded
            if (
              this.selectedDate &&
              this.selectedTime &&
              this.staffs.length > 0
            ) {
            this.checkStaffAvailability();
          }
        } else {
          this.selectedServices = [];
        }
      })
    );
  }

  onStaffSelect(serviceId: string, staffId: string | null): void {
    const selectedService = this.selectedServices.find(
      (s) => s.serviceId === serviceId
    );
    if (!selectedService) return;

    if (!staffId || staffId === null || staffId === '') {
      // Clear staff selection
      selectedService.staffId = undefined;
      selectedService.staffName = undefined;
      this.updateCart();
      return;
    }

    const staff = this.staffs.find((s) => s.id === staffId);
    if (!staff) return;

    // Update the selected service immediately
    selectedService.staffId = staff.id;
    selectedService.staffName =
      staff.nickname || `${staff.firstName} ${staff.lastName}`;

    // Regenerate available times when staff is selected
    if (this.selectedDate) {
      this.generateAvailableTimes();
    }

    // Update cart immediately
    this.updateCart();
  }

  onDateSelect(date: Date): void {
    this.selectedDate = date;
    // Update the ngModel value to keep it in sync
    this.dateInputValue = this.formatDateForInput(date);
    this.selectedTime = null;
    this.staffAvailability.clear(); // Clear availability when date changes
    this.generateAvailableTimes();
    // Update cart with new date (time will be null, so it won't update time yet)
    this.updateCart();
  }

  onDateChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target.value) {
      this.onDateSelect(new Date(target.value));
    }
  }

  onDateModelChange(value: string): void {
    // Handle ngModel change event
    if (value) {
      this.onDateSelect(new Date(value));
    } else {
      // Clear date if input is empty
      this.selectedDate = null;
      this.selectedTime = null;
      this.availableTimes = [];
      this.staffAvailability.clear();
      this.updateCart();
    }
  }

  onDateInputTouchStart(event: TouchEvent): void {
    // Handle touchstart - don't prevent default to allow native behavior
    const input = event.target as HTMLInputElement;
    if (input && input.type === 'date') {
      // Only stop propagation, don't prevent default
      event.stopPropagation();
      // Focus immediately on touchstart for better iOS response
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent || navigator.vendor);
      if (isIOS) {
        input.focus();
      }
    }
  }

  onDateInputTouchEnd(event: TouchEvent): void {
    // Handle touchend event for iOS compatibility
    const input = event.target as HTMLInputElement;
    if (input && input.type === 'date') {
      event.stopPropagation();
      
      // Get touch coordinates to determine if click was on input or icon
      const touch = event.changedTouches[0];
      const rect = input.getBoundingClientRect();
      const clickX = touch.clientX - rect.left;
      const clickY = touch.clientY - rect.top;
      
      // On iOS, ensure the input is focused and ready for interaction
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent || navigator.vendor);
      
      setTimeout(() => {
        input.focus();
        
        // Try to open the date picker programmatically if available (iOS 15.4+)
        if (isIOS) {
          const inputWithPicker = input as HTMLInputElement & { showPicker?: () => void };
          if ('showPicker' in inputWithPicker && typeof inputWithPicker.showPicker === 'function') {
            try {
              inputWithPicker.showPicker();
            } catch (e) {
              // showPicker not supported or failed, fall back to default behavior
              input.click();
            }
          } else {
            // For older iOS, trigger click event at the touch location
            const clickEvent = new MouseEvent('click', {
              bubbles: true,
              cancelable: true,
              view: window,
              clientX: touch.clientX,
              clientY: touch.clientY
            });
            input.dispatchEvent(clickEvent);
            // Also try regular click as fallback
            input.click();
          }
        }
      }, 10);
    }
  }

  onDateInputClick(event: MouseEvent): void {
    // Handle regular click events
    const input = event.target as HTMLInputElement;
    if (input && input.type === 'date') {
      event.stopPropagation();
      
      // Ensure input is focused
      input.focus();
      
      // On iOS, try to open picker programmatically
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent || navigator.vendor);
      if (isIOS) {
        setTimeout(() => {
          const inputWithPicker = input as HTMLInputElement & { showPicker?: () => void };
          if ('showPicker' in inputWithPicker && typeof inputWithPicker.showPicker === 'function') {
            try {
              inputWithPicker.showPicker();
            } catch (e) {
              // Fallback to native behavior
            }
          }
        }, 10);
      }
    }
  }

  isServiceSelected(serviceId: string): boolean {
    return (
      this.selectedServices.find((s) => s.serviceId === serviceId) !== undefined
    );
  }

  onTimeSelect(time: string): void {
    this.selectedTime = time;
    // Check staff availability when time is selected
    if (this.selectedDate && this.staffs.length > 0) {
      this.checkStaffAvailability();
    }
    // Update all cart items with the selected time
    this.updateCart();
  }

  generateAvailableTimes(): void {
    if (!this.store || !this.selectedDate) {
      this.availableTimes = [];
      return;
    }

    const weekDay = this.getWeekDay(this.selectedDate);
    const dayHours = this.store.workingHours[weekDay];

    if (!dayHours || dayHours.closed) {
      this.availableTimes = [];
      return;
    }

    // Check if selected date is today
    const today = new Date();
    const isToday = this.isSameDay(this.selectedDate, today);

    const times: string[] = [];
    const [openHour, openMin] = dayHours.open.split(':').map(Number);
    const [closeHour, closeMin] = dayHours.close.split(':').map(Number);

    let currentHour = isToday ? today.getHours() + 1 : openHour;
    const currentMin = isToday ? today.getMinutes() : 0;

    // Round up to next 30-minute interval if needed
    if (currentMin > 0) {
      currentHour += 1;
    }

    while (
      currentHour < closeHour ||
      (currentHour === closeHour && 0 < closeMin)
    ) {
      const timeStr = `${String(currentHour).padStart(2, '0')}:00`;
      times.push(timeStr);
      currentHour += 1;
    }

    this.availableTimes = times;
  }

  getWeekDay(date: Date): keyof BusinessInformationEntity['workingHours'] {
    const days = [
      'sunday',
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
    ];
    return days[
      date.getDay()
    ] as keyof BusinessInformationEntity['workingHours'];
  }

  isSameDay(date1: Date, date2: Date): boolean {
    return (
      date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }

  /**
   * Check if staff is working at the selected time based on their working hours
   */
  isStaffWorkingAtTime(
    staff: StaffEntity,
    date: Date,
    time: string,
    duration: number
  ): boolean {
    if (!staff || !staff.workingHours) {
      return false;
    }

    const weekDay = this.getStaffWeekDay(date);
    const dayHours = staff.workingHours[weekDay];

    if (!dayHours || dayHours.closed) {
      return false;
    }

    // Parse time
    const [hours, minutes] = time.split(':').map(Number);
    const appointmentStart = new Date(date);
    appointmentStart.setHours(hours, minutes || 0, 0, 0);

    // Calculate appointment end time
    const appointmentEnd = new Date(appointmentStart);
    appointmentEnd.setMinutes(appointmentEnd.getMinutes() + duration);

    // Parse staff working hours
    const [openHour, openMin] = dayHours.open.split(':').map(Number);
    const [closeHour, closeMin] = dayHours.close.split(':').map(Number);

    const staffStart = new Date(date);
    staffStart.setHours(openHour, openMin || 0, 0, 0);

    const staffEnd = new Date(date);
    staffEnd.setHours(closeHour, closeMin || 0, 0, 0);

    // Check if appointment fits within staff working hours
    return (
      appointmentStart.getTime() >= staffStart.getTime() &&
      appointmentEnd.getTime() <= staffEnd.getTime()
    );
  }

  /**
   * Get weekday name for staff working hours (lowercase)
   */
  private getStaffWeekDay(date: Date): keyof StaffEntity['workingHours'] {
    const days: (keyof StaffEntity['workingHours'])[] = [
      'sunday',
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
    ];
    return days[date.getDay()];
  }

  checkStaffAvailability(): void {
    if (
      !this.selectedDate ||
      !this.selectedTime ||
      this.staffs.length === 0 ||
      !this.storeId
    ) {
      return;
    }

    if (this.isLoadingAvailability) {
      return; // Already checking
    }

    this.isLoadingAvailability = true;
    this.staffAvailability.clear();

    // Create datetime from selected date and time
    const [hours, minutes] = this.selectedTime.split(':').map(Number);
    const appointmentDateTime = new Date(this.selectedDate);
    appointmentDateTime.setHours(hours, minutes || 0, 0, 0);

    // Get max duration from selected services
    const totalDuration = Math.max(
      ...this.selectedServices.map((service) => service.duration),
      60 // Default to 60 minutes if no services
    );

    // Calculate end time
    const endDateTime = new Date(appointmentDateTime);
    endDateTime.setMinutes(endDateTime.getMinutes() + totalDuration);

    // Get staff IDs
    const staffIds = this.staffs.map((staff) => staff.id);

    if (staffIds.length === 0) {
      this.isLoadingAvailability = false;
      return;
    }

    // Check availability for each staff member
    this.subscriptions.add(
      this.appointmentService
        .getStaffAvailability(
          staffIds,
          appointmentDateTime.toISOString(),
          this.storeId!
        )
        .pipe(
          catchError((error) => {
            console.error('Error checking staff availability:', error);
            this.isLoadingAvailability = false;
            // On error, mark all staff as available (fail open)
            staffIds.forEach((staffId) => {
              this.staffAvailability.set(staffId, true);
            });
            return of({});
          })
        )
        .subscribe((availabilityMap) => {
          // Check if each staff is available at the selected time
          staffIds.forEach((staffId) => {
            const staff = this.staffs.find((s) => s.id === staffId);
            
            // First check if staff is working at this time (working hours check)
            const isWorking = staff
              ? this.isStaffWorkingAtTime(
              staff,
              this.selectedDate!,
              this.selectedTime!,
              totalDuration
                )
              : false;

            if (!isWorking) {
              // Staff is not working at this time
              this.staffAvailability.set(staffId, false);
              return;
            }

            // Then check appointment availability
            const availableSlots = availabilityMap[staffId] || [];
            
            if (availableSlots.length === 0) {
              // No available slots means staff is busy
              this.staffAvailability.set(staffId, false);
              return;
            }

            // Convert available slots to Date objects and sort them
            const sortedSlots = availableSlots
              .map((slot: Date | string) =>
                typeof slot === 'string' ? new Date(slot) : slot
              )
              .sort((a, b) => a.getTime() - b.getTime());

            // Check if the appointment time can fit within consecutive available slots
            const slotDuration = 30 * 60 * 1000; // 30 minutes in milliseconds
            const appointmentDuration = totalDuration * 60 * 1000; // Convert minutes to milliseconds
            const slotsNeeded = Math.ceil(appointmentDuration / slotDuration);

            let isAvailable = false;

            // Find the slot that contains the appointment start time
            const startSlotIndex = sortedSlots.findIndex((slot) => {
              const slotEnd = new Date(slot.getTime() + slotDuration);
              return (
                appointmentDateTime >= slot && appointmentDateTime < slotEnd
              );
            });

            if (
              startSlotIndex !== -1 &&
              startSlotIndex + slotsNeeded <= sortedSlots.length
            ) {
              // Check if we have enough consecutive slots
              let consecutive = true;
              for (
                let i = startSlotIndex;
                i < startSlotIndex + slotsNeeded - 1;
                i++
              ) {
                const currentSlotEnd = new Date(
                  sortedSlots[i].getTime() + slotDuration
                );
                const nextSlot = sortedSlots[i + 1];
                // Check if slots are consecutive (next slot starts when current ends)
                if (
                  Math.abs(currentSlotEnd.getTime() - nextSlot.getTime()) > 1000
                ) {
                  consecutive = false;
                  break;
                }
              }

              if (consecutive) {
                // Check if appointment end time fits within the last slot
                const lastSlot = sortedSlots[startSlotIndex + slotsNeeded - 1];
                const lastSlotEnd = new Date(lastSlot.getTime() + slotDuration);
                isAvailable = endDateTime.getTime() <= lastSlotEnd.getTime();
              }
            }

            // Staff is available only if both working hours and appointment availability check pass
            this.staffAvailability.set(staffId, isAvailable);
          });

          this.isLoadingAvailability = false;
        })
    );
  }

  getAvailableStaffForService(serviceId: string): StaffEntity[] {
    // Filter staff based on selected date and time availability
    if (!this.selectedDate || !this.selectedTime) {
      return this.staffs;
    }

    // Get service duration
    const selectedService = this.selectedServices.find(
      (s) => s.serviceId === serviceId
    );
    const serviceDuration = selectedService?.duration || 60;

    // Filter to only available staff (both working hours and appointment availability)
    return this.staffs.filter((staff) => {
      // First check if staff is working at this time
      const isWorking = this.isStaffWorkingAtTime(
        staff,
        this.selectedDate!,
        this.selectedTime!,
        serviceDuration
      );

      if (!isWorking) {
        return false;
      }

      // Then check appointment availability
      const isAvailable = this.staffAvailability.get(staff.id);
      // If availability hasn't been checked yet, show the staff (will be filtered once checked)
      return isAvailable !== false;
    });
  }

  getUnavailableStaffForService(serviceId: string): StaffEntity[] {
    if (!this.selectedDate || !this.selectedTime) {
      return [];
    }

    // Get service duration
    const selectedService = this.selectedServices.find(
      (s) => s.serviceId === serviceId
    );
    const serviceDuration = selectedService?.duration || 60;

    return this.staffs.filter((staff) => {
      // Check if staff is not working at this time
      const isWorking = this.isStaffWorkingAtTime(
        staff,
        this.selectedDate!,
        this.selectedTime!,
        serviceDuration
      );

      if (!isWorking) {
        return true; // Staff is unavailable due to working hours
      }

      // Check appointment availability
      return this.staffAvailability.get(staff.id) === false;
    });
  }

  hasUnavailableStaffForService(serviceId: string): boolean {
    if (!this.selectedDate || !this.selectedTime) {
      return false;
    }

    // Get service duration
    const selectedService = this.selectedServices.find(
      (s) => s.serviceId === serviceId
    );
    const serviceDuration = selectedService?.duration || 60;

    return this.staffs.some((staff) => {
      // Check if staff is not working at this time
      const isWorking = this.isStaffWorkingAtTime(
        staff,
        this.selectedDate!,
        this.selectedTime!,
        serviceDuration
      );

      if (!isWorking) {
        return true; // Staff is unavailable due to working hours
      }

      // Check appointment availability
      return this.staffAvailability.get(staff.id) === false;
    });
  }

  getStaffDisplayName(staff: StaffEntity): string {
    return (
      staff.nickname ||
      `${staff.firstName} ${staff.lastName}`.trim() ||
      'Unknown Staff'
    );
  }

  removeService(serviceId: string): void {
    // Find and remove the cart item for this service
    if (!this.storeId) return;

    this.ngRxStore
      .select(CartSelectors.selectStoreCart(this.storeId))
      .pipe(
        map((storeCart) => {
        if (storeCart) {
            const itemToRemove = storeCart.items.find((item) => {
              const service =
                item.combos && item.combos.length > 0 ? item.combos[0] : null;
            return service?.id === serviceId;
          });
          if (itemToRemove) {
              this.ngRxStore.dispatch(
                CartActions.removeCartItem({
              storeId: this.storeId!, 
                  itemId: itemToRemove.id,
                })
              );
          }
        }
      }),
      take(1)
      )
      .subscribe();

    // Also remove from local selectedServices
    this.selectedServices = this.selectedServices.filter(
      (s) => s.serviceId !== serviceId
    );
  }

  updateCart(): void {
    if (!this.storeId || !this.store) {
      return;
    }

    // Update existing cart items with date, time, and staff information
    this.ngRxStore
      .select(CartSelectors.selectStoreCart(this.storeId))
      .pipe(
        map((storeCart) => {
        if (storeCart && storeCart.items.length > 0) {
          storeCart.items.forEach((item) => {
            // Find matching selected service by service ID
              const service =
                item.combos && item.combos.length > 0 ? item.combos[0] : null;
            if (!service) return;
            
              const selectedService = this.selectedServices.find(
                (s) => s.serviceId === service.id
              );
            
            const updates: Partial<CartItem> = {};

            // Update date and time if selected
            if (this.selectedDate && this.selectedTime) {
              const [hour, minute] = this.selectedTime.split(':').map(Number);
              const appointmentDateTime = new Date(this.selectedDate);
              appointmentDateTime.setHours(hour, minute, 0, 0);
              updates.date = appointmentDateTime;
              updates.startTime = appointmentDateTime;
            } else if (this.selectedDate) {
              // Only date selected, update date but keep existing time or use default
                const existingTime = item.startTime
                  ? typeof item.startTime === 'string'
                    ? new Date(item.startTime)
                    : item.startTime
                  : new Date();
              const appointmentDateTime = new Date(this.selectedDate);
                appointmentDateTime.setHours(
                  existingTime.getHours(),
                  existingTime.getMinutes(),
                  0,
                  0
                );
              updates.date = appointmentDateTime;
              updates.startTime = appointmentDateTime;
            }

            // Update staff if selected for this service
            if (selectedService && selectedService.staffId) {
              updates.staffId = selectedService.staffId;
              updates.staffName = selectedService.staffName;
            }

            // Only dispatch update if there are changes
            if (Object.keys(updates).length > 0) {
                this.ngRxStore.dispatch(
                  CartActions.updateCartItem({
                storeId: this.storeId!,
                itemId: item.id,
                    updates,
                  })
                );
            }
          });
        }
      }),
      take(1)
      )
      .subscribe();
  }

  canProceedToCheckout(): boolean {
    return (
      this.selectedServices.length > 0 &&
      this.selectedServices.every((s) => s.staffId) &&
           this.selectedDate !== null && 
      this.selectedTime !== null
    );
  }

  proceedToCheckout(): void {
    // If booking form is complete, use that; otherwise proceed with cart
    if (this.canProceedToCheckout()) {
      this.updateCart();
      // Small delay to ensure cart is updated
      setTimeout(() => {
        this.router.navigate(['/checkout']);
      }, 100);
    } else {
      // Proceed with existing cart items
      this.router.navigate(['/checkout']);
    }
  }

  getMinDate(): string {
    const today = new Date();
    return today.toISOString().split('T')[0];
  }

  formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  formatDuration(duration: number): string {
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${minutes}m`;
    }
  }

  removeCartItem(storeId: string, itemId: string): void {
    this.ngRxStore.dispatch(CartActions.removeCartItem({ storeId, itemId }));
  }

  clearCart(): void {
    this.ngRxStore.dispatch(CartActions.clearCart());
  }

  formatPrice(price: number): string {
    return `$${price.toFixed(2)}`;
  }

  formatDate(date: Date | string | null | undefined): string {
    if (!date) {
      return '';
    }

    let appointmentDate: Date;

    if (typeof date === 'string') {
      appointmentDate = new Date(date);
    } else if (date instanceof Date) {
      appointmentDate = date;
    } else {
      appointmentDate = new Date(date as any);
    }

    if (isNaN(appointmentDate.getTime())) {
      return '';
    }

    return appointmentDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  formatTime(date: Date | string | null | undefined): string {
    if (!date) {
      return '';
    }

    let appointmentDate: Date;

    if (typeof date === 'string') {
      appointmentDate = new Date(date);
    } else if (date instanceof Date) {
      appointmentDate = date;
    } else {
      appointmentDate = new Date(date as any);
    }

    if (isNaN(appointmentDate.getTime())) {
      return '';
    }

    return appointmentDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  getItemDescription(item: CartItem): string {
    const parts: string[] = [];
    
    // Add combo names
    if (item.combos && item.combos.length > 0) {
      const comboNames = item.combos.map((combo) => combo.name).join(', ');
      parts.push(comboNames);
    }
    
    // Add item names
    if (item.items && item.items.length > 0) {
      const itemNames = item.items.map((item) => item.name).join(', ');
      if (parts.length > 0) {
        parts.push(`+ ${itemNames}`);
      } else {
        parts.push(itemNames);
      }
    }
    
    // Add staff name
    if (item.staffName) {
      parts.push(`with ${item.staffName}`);
    }
    
    return parts.length > 0 ? parts.join(' ') : 'Service';
  }

  openDatepicker(): void {
    console.log('openDatepicker');
  }
}
